import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { formatDate, searchContent } from '@/utils/content';
import { movies } from '@/mocks/movies';

export default function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const keyword = searchParams.get('keyword') ?? '';
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<'movie' | 'news'>('movie');

  const { movieResults, newsResults } = useMemo(() => searchContent(keyword), [keyword]);
  const total = movieResults.length + newsResults.length;
  const hotMovies = [...movies].sort((a, b) => b.heat - a.heat).slice(0, 6);

  const submit = () => {
    const value = input.trim();
    if (!value) return;
    navigate(`/search?keyword=${encodeURIComponent(value)}`);
  };

  const activeResults = tab === 'movie' ? movieResults : newsResults;

  return (
    <PageLayout>
      <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />

        <div className="mt-6 rounded-lg bg-foreground-950 px-6 py-8 md:px-10 md:py-10">
          <h1 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">搜索影视内容</h1>
          <p className="mt-2 text-sm text-foreground-300">输入片名、导演、演员或关键词，快速找到相关影视作品与资讯。</p>
          <div className="mt-5 flex h-12 max-w-2xl items-center gap-2 rounded-full bg-background-50 px-4">
            <i className="ri-search-line text-lg text-foreground-500" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
              type="text"
              placeholder="例如：悬疑、科幻、长夜将尽"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
            <button
              type="button"
              onClick={submit}
              className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </div>
        </div>

        {keyword ? (
          <>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-foreground-600">
                关键词 <span className="font-semibold text-primary-600">“{keyword}”</span> 共找到{' '}
                <span className="font-semibold text-foreground-950">{total}</span> 条结果
              </p>
              <div className="flex items-center gap-1 rounded-full bg-background-200 p-1">
                <button
                  type="button"
                  onClick={() => setTab('movie')}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                    tab === 'movie' ? 'bg-primary-500 text-background-50' : 'text-foreground-600'
                  }`}
                >
                  影视作品 ({movieResults.length})
                </button>
                <button
                  type="button"
                  onClick={() => setTab('news')}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                    tab === 'news' ? 'bg-primary-500 text-background-50' : 'text-foreground-600'
                  }`}
                >
                  资讯 ({newsResults.length})
                </button>
              </div>
            </div>

            {activeResults.length > 0 ? (
              tab === 'movie' ? (
                <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {movieResults.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="mt-6 flex flex-col gap-4">
                  {newsResults.map((article) => (
                    <Link
                      key={article.id}
                      to={`/news/${article.id}`}
                      className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200 sm:flex-row"
                    >
                      <div className="h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-28 sm:w-44">
                        <img
                          src={article.cover}
                          alt={article.title}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
                          {article.category}
                        </span>
                        <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-600">
                          {article.title}
                        </h3>
                        <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                          {article.excerpt}
                        </p>
                        <p className="mt-2 text-xs text-foreground-500">
                          {formatDate(article.date)} · {article.author}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )
            ) : (
              <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-emotion-sad-line text-3xl" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">没有找到相关内容</h3>
                <p className="mt-2 text-sm text-foreground-500">
                  换个关键词试试，或者到分类页浏览我们为你精选的作品。
                </p>
                <Link
                  to="/category"
                  className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  浏览全部影视
                </Link>
              </div>
            )}
          </>
        ) : (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-line text-3xl" />
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">开始你的影视探索</h3>
            <p className="mt-2 text-sm text-foreground-500">在上方输入关键词，搜索你感兴趣的影视作品或资讯内容。</p>
          </div>
        )}

        <section className="mt-12">
          <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950">大家都在看</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {hotMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>
      </div>
    </PageLayout>
  );
}